import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { CoreParameterModel } from '@fi-shared/models';
import { RoleService } from '@fi-app/role/services/role.service';
import { FiChecklistEditAddCheckpointConfirmComponent } from '../add-checkpoint-confirm/add-checkpoint-confirm.component';
import { FiChecklistSectionsDirective } from '../../common/checklist-sections.directive';
import { FiChecklistEditComponent } from '../edit.component';
import { ChecklistEditCheckpointNameComponent } from '../checkpoint-name/checkpoint-name.component';
import { ControlContainer, NgForm } from '@angular/forms';
import { ChecklistService } from '../../checklist.service';
import { CheckpointModel } from '../../checkpoint.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IFiChecklistCheckpointModel } from '@fi-shared/checklist';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { ObjectTypes } from '@fi-shared/types';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';

@Component({
    selector: 'fi-edit-checklist-content',
    templateUrl: './content.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./content.component.scss'],
    providers: [{ provide: FiComponentsContainer, useExisting: FiChecklistEditContentComponent }],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: NgForm,
        },
        {
            provide: 'defaultLanguageId',
            useFactory: (self: FiChecklistEditContentComponent) => self.defaultLanguageId,
            deps: [FiChecklistEditContentComponent],
        },
    ],
    standalone: false,
})
export class FiChecklistEditContentComponent extends FiComponentsContainer implements OnInit, OnDestroy {
    @Input() checklist: ChecklistModel;
    @Input() selectedLanguageId;
    @Input() defaultLanguageId: number;
    @Input() contentChanged$: Subject<void>;

    @ViewChild('checklistSectionsCtrl', { static: false }) checklistSectionsCtrl: FiChecklistSectionsDirective;
    @ViewChildren(ChecklistEditCheckpointNameComponent)
    checklistEditCheckpointNames: QueryList<ChecklistEditCheckpointNameComponent>;
    focusedNodeElement: HTMLElement;

    constructor(
        private appTextService: AppTextService,
        private rolesService: RoleService,
        private fiDialogsService: FiDialogsService,
        private checklistService: ChecklistService,
        private stateService: FiStateService,
        public cdr: ChangeDetectorRef,
        private customerDataService: FiCustomerDataService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private textEditorService: TextEditorService,
        private fiInsertMedia2Service: FiInsertMedia2Service,
        @Optional() @Inject(FiChecklistEditComponent) private checklistEdit: FiChecklistEditComponent,
    ) {
        super();
    }

    ngOnInit() {
        this.checklist.CheckPoints.forEach(function (checkpoint) {
            checkpoint.$$notifyRoleChecked = checkpoint.NotificationRoleId !== null;
        });
        this.loadRoles();

        if (this.checklistEdit) {
            this.checklistEdit.templateChanged$.subscribe(() => {
                this.checklistSectionsCtrl.rebuild();
            });
        }
        if (this.contentChanged$) {
            this.contentChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.cdr.markForCheck());
        }
        this.fiBrowserEventsService
            .getWindowEventSubject$('keydown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: KeyboardEvent) => this.textEditorService.handleTabKeyPress($event));
    }

    setFocusedElement() {
        this.focusedNodeElement = this.textEditorService.getFocusedElement();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    isAnyCheckpointsTranslatedTranslated(): boolean {
        return this.checklist.CheckPoints.some((checkpoint: IFiChecklistCheckpointModel) => {
            const selectedLandLabelModel = this.getSelectedLangIdCheckpoint(checkpoint);
            return selectedLandLabelModel && selectedLandLabelModel.Value;
        });
    }

    getSelectedLangIdCheckpoint(checkpoint: IFiChecklistCheckpointModel): IContentLanguageLabelModel {
        return checkpoint.Names.find((checkpointName: IContentLanguageLabelModel) => {
            return checkpointName.ContentLanguageId === this.selectedLanguageId;
        });
    }

    isChecklistTranslated(): boolean {
        return this.checklist.CheckPoints.some((checkpoint: IFiChecklistCheckpointModel) => {
            return checkpoint.Names.some((checkpointName: IContentLanguageLabelModel) => checkpointName.Value);
        });
    }

    isCheckpointNameRequired(checkpointName): boolean {
        return (
            !this.isChecklistTranslated() ||
            (this.isAnyCheckpointsTranslatedTranslated() &&
                checkpointName.ContentLanguageId === this.selectedLanguageId)
        );
    }

    getCheckpointNamePlaceholder(name, names) {
        for (let i = 0; i < names.length; i++) {
            if (names[i].ContentLanguageId === this.defaultLanguageId) {
                return names[i].Value;
            }
        }
    }

    getDescriptionPlaceholder(description, descriptions) {
        for (let i = 0; i < descriptions.length; i++) {
            if (descriptions[i].ContentLanguageId === this.defaultLanguageId) {
                return descriptions[i].Value;
            }
        }
    }

    setNamesTouched(item) {
        this.checklistEditCheckpointNames.forEach((name) => {
            if (name.contentLanguageId == item.ContentLanguageId) {
                name.setTouched();
            }
        });
    }

    isNameTouched(item) {
        if (!this.checklistEditCheckpointNames) {
            return false;
        }

        const nameModel = this.checklistEditCheckpointNames.find(
            (name) => name.contentLanguageId == item.ContentLanguageId,
        );
        return nameModel && nameModel.touched;
    }

    nameControlChange(name) {
        this.setNamesTouched(name);
    }

    descriptionControlChange(description) {
        this.setNamesTouched(description);
        this.setFocusedElement();

        // error message on firm name doesn't disappear for some reason until next "tick". Trigger setTimeout to update it
        setTimeout(() => {}, 10);
    }

    openInsertMediaDialog(checkpoint: IFiChecklistCheckpointModel) {
        this.fiInsertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.CheckPoint)
            .onYesButtonClick((result) => {
                checkpoint.MediaBankFileReferences = checkpoint.MediaBankFileReferences.concat(
                    result.mediaBankFileReferences,
                );
                this.cdr.markForCheck();
            });
    }

    isTemplateViewState() {
        return this.stateService.current.name === FiStateNames.Checklist.Template.View;
    }

    isSortingDisabled() {
        if (this.isTemplateViewState()) return true;

        if (this.checklist.IsTemplate || !this.checklist.Id) return false;

        return !(
            this.checklist.HasEditAccess /* &&
                !this.checklist.IsPublished && !this.checklist.IsWithResponses*/
        );
    }

    getSortingDisabledTitle() {
        if (!this.isSortingDisabled()) return '';

        /*if (this.checklist.CreatedByUserId !== CcmSessionService.getUser().Id) {
            return this.appTextService.get('checklist.sorting_not_an_author');
        }*/

        /* if (this.checklist.IsPublished && !this.checklist.IsWithResponses) {
             return this.appTextService.get('checklist.sorting_unpublish');
         }

         if (this.checklist.IsPublished && this.checklist.IsWithResponses) {
             return this.appTextService.get('checklist.sorting_unpublish_and_delete_responses');
         }*/

        if (/*!this.checklist.IsPublished && */ this.checklist.IsWithResponses)
            return this.appTextService.get('checklist.sorting_delete_responses');
    }

    isAddCheckpointDisabled() {
        return this.isTemplateViewState() || this.checklist.HasEditAccess === false;
    }

    isDeleteCheckpointSectionDisabled() {
        return this.isAddCheckpointDisabled();
    }

    isDeleteDisabled() {
        return this.checklist.CheckPoints.length === 1 || this.isSortingDisabled();
    }

    isCheckPresenceEnabledForCustomer() {
        return this.customerDataService.customerData.ChecklistPresenceEnabled;
    }

    isCheckPresenceEnabledForChecklist() {
        return this.checklist.CheckPresenceEnabled;
    }

    RolesToNotify = [];

    loadRoles() {
        const params = new CoreParameterModel();
        params.NumberOfRecordsToTake = -1;
        params.SelectedSort = {
            ColumnName: 'Name',
            SortDirection: false,
        };
        this.rolesService.getRoles(params).then((data) => {
            this.RolesToNotify = data.map((role) => ({ Id: role.Id, Name: role.Name }));
        });
    }

    // store last added checkpoint to autofocus it after add and not autofocus any field on init
    lastAddedCheckpoint: CheckpointModel;

    addCheckpoint(section) {
        this.addCheckpointAskUser().then(() => {
            this.lastAddedCheckpoint = this.checklistService.getCheckpointInstance();
            section.CheckPoints.push(this.lastAddedCheckpoint);
            this.checklistSectionsCtrl.updateCheckpointsFromSections();
            this.cdr.markForCheck();
        });
    }

    addCheckpointAskUser(): Promise<void> {
        if (!this.checklist.IsWithResponses) {
            return Promise.resolve();
        }
        return new Promise<void>((resolve, reject) => {
            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('dialog.header_confirm'),
                    contentComponentRef: FiChecklistEditAddCheckpointConfirmComponent,
                })
                .onYesButtonClick(resolve)
                .onNoButtonClick(reject);
        });
    }
}
